import { ensureNoUndefinedProperties } from '@/Shared/Helper/EnsureNoUndefinedProperties'
import Customer from '@/App/Customer/Entity/Customer'
import BookingPayment from '@/App/Booking/Payment/Entity/BookingPayment'
import Reservation from '@/App/Booking/Reservation/Entity/Reservation'
import CustomFieldResponseData from '@/App/CustomField/Entity/CustomFieldResponseData'
import {
    BookingPaymentStateEnum,
    type ReservationDataRawData,
} from '@/Shared/AutoGenerated/Main'
import type HasPaymentStateInterface from '@/App/Booking/Shared/Entity/HasPaymentStateInterface'

export default class ReservationData implements HasPaymentStateInterface {
    constructor(
        public readonly reservation: Reservation,
        public readonly bookingPayment: BookingPayment | null,
        public readonly customer: Customer,
        public readonly customFieldResponses: CustomFieldResponseData[],
    ) {
        ensureNoUndefinedProperties(this)
    }

    isPaid(): boolean {
        return this.bookingPayment !== null
    }

    hasCustomFieldResponses(): boolean {
        return this.customFieldResponses.length > 0
    }

    // ------------------------
    // HasPaymentStateInterface implementation
    // ------------------------

    getPaymentStateBadgeColor(): 'info' | 'secondary' {
        if (!this.bookingPayment) {
            throw new Error('Booking is not paid')
        }

        if (this.bookingPayment.state === BookingPaymentStateEnum.Paid) {
            return 'info'
        }

        if (this.bookingPayment.state === BookingPaymentStateEnum.Refunded) {
            return 'secondary'
        }

        throw new Error(
            `Unknown booking payment state ${this.bookingPayment.state}`,
        )
    }

    getPaymentStateBadgeLabel(): 'Paid' | 'Refunded' {
        if (!this.bookingPayment) {
            throw new Error(`Reservation is not paid ${this.reservation.id}`)
        }

        return this.bookingPayment?.state
    }

    // ------------------------
    // Hydration
    // ------------------------

    static hydrateSingle(entityData: ReservationDataRawData): ReservationData {
        return new ReservationData(
            Reservation.hydrateSingle(entityData.reservationRawData),
            BookingPayment.hydrateSingleOrNull(
                entityData.bookingPaymentRawData,
            ),
            Customer.hydrateSingle(entityData.customerRawData),
            CustomFieldResponseData.hydrateMultiple(
                entityData.customFieldResponsesRawData,
            ),
        )
    }

    static hydrateMultiple(
        entitiesData: ReservationDataRawData[],
    ): ReservationData[] {
        return entitiesData.map((entityData) => this.hydrateSingle(entityData))
    }
}
